/**
 * ComparableCompaniesActionButtons - Shared action buttons for comparable companies
 *
 * Contains:
 * - Save project button
 * - View switcher (list/heatmap/chart)
 * - Sort dropdown
 *
 * Used by both ComparableCompaniesHeader (desktop) and ComparableCompaniesHeaderMobile
 * to reduce code duplication.
 *
 * TD-1846: Wrapped with observer to ensure component re-renders when MobX
 * observables change (e.g., isSimilarCompaniesViewMenuToggled).
 */
import { observer } from 'mobx-react';
import IconButton, { ICON_BUTTON_ICONS, ICON_BUTTON_TYPES } from '../../../buttons/IconButton';
import {
  getEffectiveChartSort,
  isSortOptionVisibleInChart
} from '../../ProfilerBarChart/chartSortDefaults';
import { filterEbitSortOptions } from '../../../../pages/search/SearchResults/constants/constants';

const ComparableCompaniesActionButtons = observer(
  ({
    state,
    viewsList,
    sortOptions,
    hasExtendedAccess,
    idSuffix = 'desktop',
    filterViewsFn = null
  }) => {
    // Filter views if filter function provided (mobile filters out chart on narrow screens)
    const filteredViews = filterViewsFn ? viewsList.slice().filter(filterViewsFn) : viewsList;

    // The sort the comparables are really ordered by: the Chart view falls back to
    // EBIT-margin ↓ while nothing is chosen. Display only — applying and persisting a
    // sort stays on `selectedSimilarCompaniesSort`, so list/heatmap keep relevance order.
    const isChartView = state.selectedSimilarCompaniesView === viewsList[2]?.value;
    const effectiveSort = getEffectiveChartSort(state.selectedSimilarCompaniesSort, isChartView);

    // Early returns rather than a nested ternary (S3358); the button names the sort the
    // comparables are really in.
    const sortTooltipText = () => {
      if (state.isSimilarCompaniesViewMenuToggled || state.isSimilarCompaniesSortToggled) return '';
      if (
        state.isLoadingSimilarCompanies ||
        state.similarCompaniesInitialLoad ||
        !state.similarCompanies?.length
      )
        return 'Sort';
      if (effectiveSort === 'default') return 'Sort';
      return sortOptions.find((o) => o.value === effectiveSort)?.name || 'Sort';
    };

    return (
      <>
        {/* Save Project Button */}
        <IconButton
          id={`similar-list-save-project-btn-${idSuffix}`}
          icon={ICON_BUTTON_ICONS.FLOPPY_2_FILL}
          type={ICON_BUTTON_TYPES.BLUE}
          disabled={state.isSaveComparablesAsProjectDisabled}
          tooltipContent={state.saveComparablesAsProjectTooltip}
          tooltipClickable={state.saveComparablesAsProjectTooltipClickable}
          tooltipPosition={state.saveComparablesAsprojectTooltipPosition}
          onClick={state.toggleProfilerComparablesSaveProjectPopup}
        />

        {/* View Switcher Button */}
        <IconButton
          id={`similar-list-change-btn-${idSuffix}`}
          icon={viewsList.find((view) => view.value === state.selectedSimilarCompaniesView)?.icon}
          type={ICON_BUTTON_TYPES.BLUE}
          tooltipText={
            state.isSimilarCompaniesViewMenuToggled || state.isSimilarCompaniesSortToggled
              ? ''
              : 'Switch view'
          }
          onClick={state.toggleSimilarCompaniesViewMenu}
          filled={state.isSimilarCompaniesViewMenuToggled}
        />

        {/* View Menu Dropdown */}
        {state.isSimilarCompaniesViewMenuToggled && (
          <div className="view-menu-wrap">
            {filteredViews.map((view) => (
              <IconButton
                key={view.id}
                hoverType={ICON_BUTTON_TYPES.BLUE}
                withBorder={false}
                innerText={view.name}
                onClick={() => {
                  state.selectSimilarCompaniesView(view.value);
                  state.toggleSimilarCompaniesViewMenu();
                }}
                disabled={state.selectedSimilarCompaniesView === view.value || !!view.disabled}
                filled={state.selectedSimilarCompaniesView === view.value}
                {...(view.icon ? { iconOnRight: true, icon: view.icon } : {})}
                tooltipText={view.tooltipText || ''}
              />
            ))}
          </div>
        )}

        {/* Sort Button */}
        <IconButton
          id={`similar-sort-btn-${idSuffix}`}
          icon={
            effectiveSort !== 'default'
              ? ICON_BUTTON_ICONS.OCTI_LIST_ORDERED
              : ICON_BUTTON_ICONS.OCTI_LIST_UNORDERED
          }
          type={ICON_BUTTON_TYPES.BLUE}
          tooltipText={sortTooltipText()}
          tooltipPosition={effectiveSort !== 'default' ? 'bottom-end' : 'bottom-center'}
          disabled={
            state.isLoadingSimilarCompanies ||
            state.similarCompaniesInitialLoad ||
            !state.similarCompanies?.length ||
            state.selectedSimilarCompaniesView === viewsList[1]?.value
          }
          onClick={state.toggleSimilarCompaniesSort}
          filled={state.isSimilarCompaniesSortToggled || effectiveSort !== 'default'}
        />

        {/* Sort Menu Dropdown */}
        {state.isSimilarCompaniesSortToggled && (
          <div className="similar-sort-menu-wrap">
            {filterEbitSortOptions(sortOptions, hasExtendedAccess)
              // Chart view overrides relevance order, so that option would be a dead entry
              .filter(
                (option) =>
                  !isChartView ||
                  isSortOptionVisibleInChart(option, state.selectedSimilarCompaniesSort)
              )
              .map((option) => (
                <IconButton
                  key={option.value}
                  hoverType={ICON_BUTTON_TYPES.BLUE}
                  withBorder={false}
                  innerText={option.shortName || option.name}
                  onClick={() => {
                    state.selectSimilarCompaniesSortOption(option.value);
                    state.toggleSimilarCompaniesSort();
                  }}
                  disabled={effectiveSort === option.value}
                  filled={effectiveSort === option.value}
                  {...(option.icon ? { iconOnRight: true, icon: option.icon } : {})}
                />
              ))}
          </div>
        )}
      </>
    );
  }
);

export default ComparableCompaniesActionButtons;
